import * as Sentry from "@sentry/react";

const DEV_ENVIRONMENTS = ["dev", "test", "testing"];

const DEFAULT_TRACING_TARGETS = [
  "localhost",
  /^.*\.polyguard\.ai/,
  /^.*\.actual\.video/,
];

/**
 * Initialise Sentry for a frontend app.
 *
 * Each React app (zoom / teams / outlook / console) calls this once from its
 * entry point with its own DSN and any per-app overrides. Skips initialisation
 * entirely when REACT_APP_DEPLOY_ENV is dev/test/testing so local runs don't
 * report.
 *
 * @param {Object} opts
 * @param {string} opts.dsn                              - Required Sentry DSN
 * @param {string} [opts.environment]                    - Override; defaults to REACT_APP_DEPLOY_ENV
 * @param {boolean} [opts.enableLogs=true]               - Forwards Sentry's log capture
 * @param {boolean} [opts.sendDefaultPii=true]           - Sentry's automatic PII capture
 * @param {false|{sampleRate?: number, propagationTargets?: any[]}} [opts.tracing=false]
 *   Browser tracing is off by default (zoom + console run in embedded contexts
 *   where the tracing integration has had stability issues historically).
 *   Pass `{ sampleRate: 1.0 }` to enable it with defaults; pass an object with
 *   custom values to override.
 * @param {Array} [opts.extraIntegrations=[]]            - Additional Sentry integrations to append
 */
export function initSentry({
  dsn,
  environment,
  enableLogs = true,
  sendDefaultPii = true,
  tracing = false,
  extraIntegrations = [],
} = {}) {
  if (!dsn) {
    console.warn("initSentry called without a DSN — Sentry not initialised");
    return null;
  }

  const deployEnv = process.env.REACT_APP_DEPLOY_ENV;
  if (DEV_ENVIRONMENTS.includes(deployEnv)) {
    console.log(`Skipping Sentry Instrumentation - '${deployEnv}'`);
    return null;
  }

  const integrations = [
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ...(tracing ? [Sentry.browserTracingIntegration()] : []),
    ...extraIntegrations,
  ];

  const tracingConfig = tracing
    ? {
        tracesSampleRate: tracing.sampleRate ?? 1.0,
        tracePropagationTargets: tracing.propagationTargets ?? DEFAULT_TRACING_TARGETS,
      }
    : {};

  return Sentry.init({
    dsn,
    enableLogs,
    sendDefaultPii,
    environment: environment ?? deployEnv ?? "production",
    integrations,
    ...tracingConfig,
  });
}
